/**
 * Aplomb — observability wiring (Sentry + PostHog).
 *
 * V1 : chargement conditionnel via env vars — si absents (dev local), no-op.
 * Chargement dynamic import pour éviter d'embarquer 100 KB dans le bundle
 * initial si l'utilisateur bloque la télémétrie via _redirects ou browser
 * privacy mode.
 *
 * Config env (Cloudflare Pages ou window.__APLOMB_ENV__ injecté par SSR
 * futur) :
 *   VITE_APLOMB_SENTRY_DSN
 *   VITE_APLOMB_POSTHOG_KEY
 *   VITE_APLOMB_POSTHOG_HOST (défaut https://eu.i.posthog.com)
 */

interface Config {
  sentryDsn?: string;
  posthogKey?: string;
  posthogHost?: string;
  environment: string;
  release?: string;
}

function readConfig(): Config {
  // import.meta.env est populé par Vite au build.
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  return {
    sentryDsn: env.VITE_APLOMB_SENTRY_DSN,
    posthogKey: env.VITE_APLOMB_POSTHOG_KEY,
    posthogHost: env.VITE_APLOMB_POSTHOG_HOST ?? "https://eu.i.posthog.com",
    environment: env.MODE ?? "development",
    release: env.VITE_APLOMB_RELEASE,
  };
}

let initialized = false;

export async function initObservability(): Promise<void> {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  const config = readConfig();

  // Respecte DNT (Do Not Track) — pas de télémétrie si l'utilisateur l'a activé.
  if (navigator.doNotTrack === "1") {
    console.info("[aplomb] DNT actif — télémétrie désactivée.");
    return;
  }

  // Sentry — capture erreurs + performance.
  if (config.sentryDsn) {
    try {
      const Sentry = await import("@sentry/browser");
      Sentry.init({
        dsn: config.sentryDsn,
        environment: config.environment,
        release: config.release,
        tracesSampleRate: 0.1,
        beforeSend(event) {
          // PII scrubbing minimal — retire les emails côté client au cas où.
          if (event.request?.headers) {
            delete event.request.headers.Authorization;
            delete event.request.headers.Cookie;
          }
          return event;
        },
      });
    } catch (err) {
      console.warn("[aplomb] Sentry init failed:", err);
    }
  }

  // PostHog — analytics privacy-first.
  if (config.posthogKey) {
    try {
      const posthog = (await import("posthog-js")).default;
      posthog.init(config.posthogKey, {
        api_host: config.posthogHost,
        capture_pageview: true,
        capture_pageleave: true,
        // Loi 25 QC : consentement explicite requis pour cookies non-essentiels.
        // V1 : session_recording désactivé tant que CMP pas branché.
        disable_session_recording: true,
        persistence: "localStorage+cookie",
        loaded: (ph) => {
          if (config.environment === "development") {
            ph.debug();
          }
        },
      });
    } catch (err) {
      console.warn("[aplomb] PostHog init failed:", err);
    }
  }
}

export async function captureEvent(name: string, props?: Record<string, unknown>): Promise<void> {
  if (typeof window === "undefined") return;
  const config = readConfig();
  if (!config.posthogKey) return;
  try {
    const posthog = (await import("posthog-js")).default;
    posthog.capture(name, props);
  } catch {
    // no-op silent
  }
}

export async function captureError(err: unknown, context?: Record<string, unknown>): Promise<void> {
  if (typeof window === "undefined") return;
  const config = readConfig();
  if (!config.sentryDsn) return;
  try {
    const Sentry = await import("@sentry/browser");
    Sentry.captureException(err, { extra: context });
  } catch {
    // no-op silent
  }
}
